import {DosageFormData} from "@/dosage/interfaces/DosageForm";
import DosageService from "@/dosage/services/DosageService";

/**
 * A moment of the day a take falls in, as the posology is read at a glance.
 */
export type GlanceSlot = 'morning' | 'noon' | 'evening' | 'bedtime';

/**
 * A posology drawn rather than written: what is taken at each moment of the
 * day, on the days of the week it names; a take on demand; or nothing the
 * glance can draw, which the posology then says in words.
 */
export type PosologyGlance =
    | {
        kind: 'grid';
        doses: Record<GlanceSlot, number>;
        /**
         * The days of the week the takes fall on, Sunday being 0 — null when
         * the takes come every day.
         */
        days: number[] | null;
        /**
         * The rhythm in words, for one the days do not tell: once a week.
         */
        rhythm: string | null;
    }
    | {kind: 'needed'; label: string}
    | {kind: 'text'};

export const GLANCE_SLOTS: GlanceSlot[] = ['morning', 'noon', 'evening', 'bedtime'];

/**
 * The moment of the day of a take at a fixed time.
 */
const slotOf = (time: string): GlanceSlot => {
    const [hours, minutes] = time.split(':').map(Number);
    const at = hours * 60 + (minutes || 0);

    if (at < 11 * 60) {
        return 'morning';
    }

    if (at < 15 * 60) {
        return 'noon';
    }

    if (at < 21 * 60) {
        return 'evening';
    }

    return 'bedtime';
};

const PosologyGlance = () => {
    return {
        /**
         * Read a posology at a glance. The frequencies speak the encoding of
         * the referential: `every|1|days` every day, `days|1-3-5` on given
         * days of the week, `every|1|weeks` once a week, `never` on demand.
         * Any other rhythm — every other day, monthly, by cycles — is left to
         * words.
         */
        of(data: DosageFormData): PosologyGlance {
            const takes = data.times.filter(time => time.dose > 0);
            const frequency = data.frequency?.frequency ?? 'every|1|days';

            const triggered = takes.find(DosageService.isDosageTimeWithTrigger);

            if (frequency === 'never' || triggered) {
                return {kind: 'needed', label: triggered?.dosage_time_trigger.name ?? data.frequency?.name ?? 'Si besoin'};
            }

            if (takes.length === 0) {
                return {kind: 'text'};
            }

            let days: number[] | null = null;
            let rhythm: string | null = null;

            if (frequency.startsWith('days|')) {
                days = frequency.slice('days|'.length).split('-').map(Number);
            } else if (frequency === 'every|1|weeks') {
                rhythm = data.frequency?.name ?? '1x par sem.';
            } else if (frequency !== 'every|1|days') {
                return {kind: 'text'};
            }

            const doses: Record<GlanceSlot, number> = {morning: 0, noon: 0, evening: 0, bedtime: 0};

            takes.forEach(take => {
                if ('time' in take) {
                    doses[slotOf(take.time)] += take.dose;
                }
            });

            return {kind: 'grid', doses, days, rhythm};
        }
    };
};

export default PosologyGlance();
